import type { FC } from 'react';
import { aiNotesCount } from '@/components/Agents/SendValidator/utils/sendAiUse';
import { findingsNotShown, type SendOmission } from '@/components/Agents/SendValidator/utils/sendOmission';

interface SendOmissionNoticeProps {
  omission: SendOmission;
  testId: string;
}

/** The word for a count, without the count, in the singular for exactly one. */
const wordFor = (count: number, one: string, many: string): string => (count === 1 ? one : many);

/**
 * Announces, with counts, the findings and Benchling In Vivo AI notes a limit left out of a SEND
 * result, so an omitted finding is never simply missing. Renders nothing when nothing was left out.
 */
const SendOmissionNotice: FC<SendOmissionNoticeProps> = ({ omission, testId }) => {
  const notShown = findingsNotShown(omission);
  const aiNotesNotShown = Math.max(0, omission.aiNotesNotShown);
  if (notShown === 0 && aiNotesNotShown === 0) {
    return null;
  }
  return (
    <div className="mt3 f6 dark-gray" data-test-component="SendOmissionNotice" data-testid={testId}>
      {notShown > 0 && (
        <p className="ma0" data-test-element="findings-not-shown" data-test-key={String(notShown)}>
          Showing {omission.shown} of {omission.found} {wordFor(omission.found, 'finding', 'findings')} — {notShown} not
          shown. The report carries a limited number of examples; the counts include every finding.
        </p>
      )}
      {aiNotesNotShown > 0 && (
        <p className="ma0 mt1" data-test-element="ai-notes-not-shown" data-test-key={String(aiNotesNotShown)}>
          {aiNotesCount(aiNotesNotShown)} not shown — the checks&apos; own findings take priority when space is limited.
        </p>
      )}
    </div>
  );
};

export default SendOmissionNotice;
